import React, { useMemo } from "react";
import { FormField } from "../../../../shared/components/forms/FormField";
import { Button } from "../../../../shared/components/ui/Button";
import { Card } from "../../../../shared/components/ui/Card";
import type { SwimmerType } from "../../../../api/client/types/swimmer-type.types";
import { useEditSwimmerForm } from "./useEditSwimmerForm";
import type { EditSwimmerFormData } from "./EditSwimmerForm.types";

interface EditSwimmerFormProps {
  swimmerTypes: SwimmerType[];
  onSubmit: (data: EditSwimmerFormData) => void;
  initialData?: Partial<EditSwimmerFormData>;
  isSubmitting?: boolean;
  submitLabel?: string;
  /** When true, shows admin-only registration timestamp editor */
  canEditRegistrationTimestamp?: boolean;
  /** Initial registration timestamp ISO string (from backend) */
  initialRegistrationTimestamp?: string | null;
  /** Callback to notify parent of registration timestamp changes (ISO string or null) */
  onRegistrationTimestampChange?: (value: string | null) => void;
}

export const EditSwimmerForm: React.FC<EditSwimmerFormProps> = ({
  swimmerTypes,
  onSubmit,
  initialData,
  isSubmitting = false,
  submitLabel = "Guardar cambios",
  canEditRegistrationTimestamp = false,
  initialRegistrationTimestamp,
  onRegistrationTimestampChange,
}) => {
  const swimmerTypesOptions = useMemo(
    () =>
      swimmerTypes.map((swimmerType) => ({
        label: swimmerType.name,
        value: swimmerType.id.toString(),
      })),
    [swimmerTypes]
  );

  const {
    register,
    handleSubmit,
    control,
    errors,
    isValid,
    registrationDateValue,
    registrationTimeValue,
    registrationDateDisplay,
    handleRegistrationTimestampChange,
  } = useEditSwimmerForm({
    onSubmit,
    initialData,
    initialRegistrationTimestamp,
    onRegistrationTimestampChange,
  });

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6 w-full max-w-4xl mx-auto"
    >
      <Card variant="formSection">
        <h2 className="text-lg font-medium text-gray-800 mb-4">
          Datos del bañista
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FormField
              type="input"
              label="Nombre y Apellido"
              required
              inputProps={{
                ...register("name"),
                type: "text",
                placeholder: "Nombre completo",
                disabled: isSubmitting,
                className: "text-sm",
              }}
              error={errors.name?.message}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 md:col-span-2">
            <FormField
              type="input"
              label="Torre"
              required
              inputProps={{
                ...register("building_tower"),
                type: "text",
                placeholder: "Ej: A, B, C",
                disabled: isSubmitting,
                className: "text-sm",
              }}
              error={errors.building_tower?.message}
            />

            <FormField
              type="input"
              label="Apartamento"
              required
              inputProps={{
                ...register("building_apartment"),
                type: "text",
                placeholder: "Ej: 101, 202",
                disabled: isSubmitting,
                className: "text-sm",
              }}
              error={errors.building_apartment?.message}
            />
          </div>

          <div className="md:col-span-2">
            <FormField
              type="radioGroup"
              label="Tipo de Bañista"
              required
              radioGroupProps={{
                control,
                name: "swimmer_type_id",
                options: swimmerTypesOptions,
                disabled: isSubmitting,
                layout: "horizontal",
              }}
              error={errors.swimmer_type_id?.message}
            />
          </div>

          {canEditRegistrationTimestamp && (
            <div className="md:col-span-2">
              <FormField
                type="dateTime"
                label="Fecha y Hora de Registro"
                required={false}
                dateTimeProps={{
                  currentDateDisplay:
                    registrationDateDisplay || "Sin fecha registrada",
                  timeValue: registrationTimeValue || "",
                  onTimeChange: (time) =>
                    handleRegistrationTimestampChange(
                      registrationDateValue,
                      time,
                    ),
                  dateValue: registrationDateValue || undefined,
                  onDateChange: (date) =>
                    handleRegistrationTimestampChange(
                      date,
                      registrationTimeValue,
                    ),
                  id: "registration_timestamp",
                  name: "registration_timestamp",
                }}
                // This field uses its own validation rules; no zod error
              />
            </div>
          )}
        </div>
      </Card>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-2">
        <Button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="text-sm px-8 py-3 font-semibold"
        >
          {isSubmitting ? "Guardando..." : submitLabel}
        </Button>
      </div>
    </form>
  );
};
